import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Paper, Grid, Stepper, Step, StepLabel, StepConnector, Chip, List, ListItem,
  ListItemText, ListItemSecondaryAction, IconButton, Autocomplete, Dialog,
  DialogTitle, DialogContent, DialogActions, FormControlLabel, Radio, RadioGroup,
  alpha, useTheme, Tabs, Tab, Tooltip, useMediaQuery, Avatar, FormLabel
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, isValid } from 'date-fns';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from './firebase';

// Import Icons
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import ApartmentIcon from '@mui/icons-material/Apartment';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ImageIcon from '@mui/icons-material/Image';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TimelineIcon from '@mui/icons-material/Timeline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import FlagIcon from '@mui/icons-material/Flag';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveIcon from '@mui/icons-material/Save';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';

// Safe date formatting function
export const safeFormatDate = (dateValue, formatString = 'yyyy-MM-dd') => {
  try {
    if (!dateValue) return '';
    let date;
    
    // Handle different date input types
    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      // Try to parse as regular date string first
      date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        // Try to parse as ISO string
        try {
          date = parseISO(dateValue);
          if (!isValid(date)) {
            console.warn('safeFormatDate: Invalid date string', dateValue);
            return '';
          }
        } catch (parseError) {
          console.warn('safeFormatDate: Failed to parse date string', dateValue);
          return '';
        }
      }
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else if (dateValue && dateValue.toDate) {
      // Handle Firestore Timestamp
      date = dateValue.toDate();
    } else {
      console.warn('safeFormatDate: Unrecognized date value', dateValue);
      return '';
    }
    
    // Check if date is valid
    if (!date || isNaN(date.getTime()) || date.toString() === 'Invalid Date') {
      console.warn('safeFormatDate: Invalid Date after parsing', dateValue, date);
      return '';
    }
    
    // Use native JavaScript date formatting instead of date-fns format
    if (formatString === 'yyyy-MM-dd') {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // Fallback to date-fns format only if absolutely necessary
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting date:', error, dateValue);
    return '';
  }
};

// Safe date parsing function
export const safeParseDateString = (dateString) => {
  try {
    if (!dateString) return null;
    
    const date = parseISO(dateString);
    if (isValid(date)) {
      return date;
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing date:', error, dateString);
    return null;
  }
};

// Constants
const dayOptions = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const sessionOptions = ['ניו יורק', 'לונדון', 'טוקיו', 'סידני'];
const reportOptions = [
  'אין דיווח',
  'חג',
  'אירוע חדשותי',
  'אירוע פד',
  'אירוע FOMC',
  'החלטת ריבית',
  'מדד המחירים לצרכן (CPI)',
  'ליבת מדד המחירים לצרכן',
  'מדד המחירים ליצרן (PPI)',
  'דוח תעסוקה (Non-Farm Payroll)',
  'שיעור אבטלה',
  'תוצר מקומי גולמי (GDP)',
  'מכירות קמעונאיות',
  'אמון הצרכנים',
  'דוח OPEC',
  'מלאי נפט גולמי',
  'אירוע G7',
  'סקר PMI',
  'פרוטוקול ישיבת הפד',
  'מדד מחירי הדיור',
  'הוצאות צרכנים',
  'דוח תקציב ממשלתי',
  'מכירות בתים חדשים',
  'תמ"ג',
  'הזמנות מוצרים בני-קיימא',
  'דוח תנודתיות 3',
];

const outcomeOptions = ['Take Profit', 'Stop Loss', 'Break Even'];
const triggerOptions = ['BUTTC/RBTI', 'Flight/Wing Break', 'UT/SP', 'AMD', 'אחר'];

// Fibonacci retracement levels - Extended
const fiboLevels = [
 '0.00%', '23.60%', '38.20%', '40.00%', '50.00%',
  '61.80%', '78.60%', '81.60%', '100.00%', '120.00%',
  '127.20%', '141.40%', '161.80%', '200.00%', '223.60%',
  '261.80%', '361.80%', '423.60%', '461.80%', '561.80%',
  '661.80%', '761.80%', '861.80%'
];

// TP options with half R from 0 to 100
const tpOptions = [];
for (let value = 0; value <= 100; value += 0.5) {
  tpOptions.push(`1:${value}`);
}

// Function to get TP options based on outcome
const getTPOptionsForOutcome = (outcome) => {
  if (outcome === 'Stop Loss' || outcome === 'Break Even') {
    // For Stop Loss and Break Even, include 0 as an option
    return ['1:0', ...tpOptions];
  }
  return tpOptions;
};

// Symbol options
const symbolOptions = ['BTC', 'ETH', 'XAUUSD', 'EURUSD', 'GBPUSD', 'NQ', 'אחר'];

// Timeframe and phase options
const timeframeOptions = ['1m', '3m', '5m', '15m', '1h', '4h', '12h', '1d', '3d', '1w'];
const phaseOptions = ['A', 'B', 'C', 'D', 'E', 'M', '', 'לא ברור'];

// Initial form state
const initialTradeForm = {
  date: '',
  dayOfWeek: '',
  time: '',
  triggerAppeared: 'no',
  matchedTrigger: '',
  session: '',
  symbol: 'BTC',
  reports: [],
  stopPercentage: '',
  outcome: '',
  duration: '',
  
  linkBefore: '',
  linkAfter: '',
  
  tpMilestonesReached: [],
  tpMilestoneExit: '',
  stopReason: '',
  finalTP: [],
  
  phases: {
    '3m': '',
    '5m': '',
    '15m': '',
    '1h': '',
    '4h': '',
    '1d': '',
    '1w': '',
  },
  mainTimeframe: '15m',
  tradeDirection: 'long',
  tpRecommendation: '',
  phasesWithC: [],
  
  firstSpikeCandle: '',
  internalPivotCandle: '',
  secondSpikeCandle: '',
  bossConfirmationCandle: '',
  
  closeDate: '',
  closeTime: '',
  
  // Fibonacci fields
  entryFiboLevel: '',
  startFiboLevel: '',
  reactionFiboLevels: [],
  targetFiboLevel: [],
  maxFiboLevel: '',
  
  achievedRValues: {},
  fiboRValues: {
    '1.414': '',
    '1.618': '',
    '2.236': '',
    '2.618': '',
    'max': ''
  }
};

// Helper functions
function getMissingFields(form) {
  if (!form) return ["נתוני טופס חסרים"];
  
  const missing = [];
  
  try {
    // Basic checks
    if (!form.date) missing.push("תאריך");
    if (!form.dayOfWeek) missing.push("יום בשבוע");
    if (!form.time) missing.push("שעה");
    if (!form.symbol) missing.push("סימול");
    
    // Check if there's at least one image
    if (!form.linkBefore) missing.push("לפחות תמונת גרף אחת");
    
    // Trigger specific checks
    if (form.triggerAppeared === 'yes') {
      if (!form.matchedTrigger) missing.push("טריגר תואם");
      if (!form.session) missing.push("סשן");
      if (!form.stopPercentage) missing.push("אחוז סטופ");
      if (!form.outcome) missing.push("תוצאת עסקה");
      if (!form.duration) missing.push("משך זמן (דקות)");
      
      if (!form.tpMilestonesReached || !Array.isArray(form.tpMilestonesReached) || form.tpMilestonesReached.length === 0) {
        missing.push("יעדי TP שהושגו");
      }
      
      if (!form.linkBefore) missing.push("תמונה לפני");
      if (!form.linkAfter) missing.push("תמונה אחרי");
      
      // Outcome specific checks
      if (form.outcome === 'Stop Loss') {
        if (!form.stopReason) missing.push("סיבת סטופ לוס");
      } else if (form.outcome === 'Take Profit' || form.outcome === 'Break Even') {
        if (!form.tpMilestoneExit) missing.push("נקודת יציאה");
        
        if (!form.finalTP || !Array.isArray(form.finalTP) || form.finalTP.length === 0) {
          missing.push("TP סופי");
        }
      }
      
      // New fields checks
      if (!form.mainTimeframe) missing.push("טיימפריים ראשי");
      if (!form.tradeDirection) missing.push("כיוון עסקה");
      
      // Fibonacci checks
      if (!form.entryFiboLevel) missing.push("רמת פיבונאצ'י בכניסה");
      if (!form.startFiboLevel) missing.push("רמת פיבונאצ'י התחלתית");
      
      // Ensure phases structure exists
      if (!form.phases || typeof form.phases !== 'object') {
        form.phases = {};
      }
      
      // Phase validation
      if (!['A', 'B', 'C', 'D', 'E', 'M'].includes(form.phases[form.mainTimeframe])) {
        missing.push("פאזה בטיימפריים ראשי");
      }
      
      // Ensure achievedRValues structure exists
      if (!form.achievedRValues || typeof form.achievedRValues !== 'object') {
        form.achievedRValues = {};
      }

      // Ensure fiboRValues structure exists
      if (!form.fiboRValues || typeof form.fiboRValues !== 'object') {
        form.fiboRValues = {};
      }
    }
  } catch (error) {
    console.error("שגיאה בבדיקת שדות:", error);
    missing.push("שגיאה בבדיקת שדות");
  }
  
  return missing;
}

function parseTimeStringToDate(timeStr) {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}

function formatDateToTimeString(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    if (date) {
      console.warn('formatDateToTimeString: Invalid date input', date);
    }
    return '';
  }
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function calculateTradeDuration(openDate, openTime, closeDate, closeTime) {
  if (!openDate || !openTime || !closeDate || !closeTime) {
    return '';
  }
  
  try {
    const [openYear, openMonth, openDay] = openDate.split('-').map(Number);
    const [openHour, openMinute] = openTime.split(':').map(Number);
    
    const [closeYear, closeMonth, closeDay] = closeDate.split('-').map(Number);
    const [closeHour, closeMinute] = closeTime.split(':').map(Number);
    
    const openDateTime = new Date(openYear, openMonth - 1, openDay, openHour, openMinute);
    const closeDateTime = new Date(closeYear, closeMonth - 1, closeDay, closeHour, closeMinute);
    
    // Calculate difference in minutes
    const diffInMinutes = Math.floor((closeDateTime - openDateTime) / (1000 * 60));
    
    return diffInMinutes > 0 ? diffInMinutes.toString() : '';
  } catch (error) {
    console.error("שגיאה בחישוב משך עסקה:", error);
    return '';
  }
}

// Calculate TP recommendation based on phases and timeframes
function calculateTPRecommendation(form) {
  console.log("מחשב המלצת TP, נתוני קלט:", JSON.stringify({
    mainTimeframe: form.mainTimeframe,
    tradeDirection: form.tradeDirection,
    phases: form.phases,
    entryFiboLevel: form.entryFiboLevel
  }));
  
  // Basic input validation
  if (!form.mainTimeframe || !form.phases || !form.tradeDirection) {
    return {
      recommendation: "נא למלא את כל השדות הנדרשים (טיימפריים ראשי, פאזות, כיוון)",
      phasesWithC: [],
      targetTimeframe: null,
      timeframesToAskR: [],
      targetFiboLevels: []
    };
  }
  
  // Set target Fibonacci levels based on phase
  let targetFiboLevels = [];
  
  // Set Fibonacci targets based on phase
  if (form.phases[form.mainTimeframe] === 'A' || form.phases[form.mainTimeframe] === 'M') {
    targetFiboLevels = ['100.00%', '161.80%'];
  } else if (form.phases[form.mainTimeframe] === 'B') {
    targetFiboLevels = ['100.00%', '161.80%'];
  } else if (form.phases[form.mainTimeframe] === 'C') {
    targetFiboLevels = ['100.00%', '161.80%', '223.60%'];
  } else if (form.phases[form.mainTimeframe] === 'D' || form.phases[form.mainTimeframe] === 'E') {
    targetFiboLevels = ['161.80%', '261.80%'];
  }
  
  // If main timeframe is not in valid phase - no trade entry
  if (!['A', 'B', 'C', 'D', 'E', 'M'].includes(form.phases[form.mainTimeframe])) {
    return {
      recommendation: "לא ניתן להיכנס לעסקה - הטיימפריים הראשי חייב להיות בפאזה תקינה",
      phasesWithC: [],
      targetTimeframe: null,
      timeframesToAskR: [],
      targetFiboLevels: []
    };
  }
  
  // Get index of main timeframe in timeframeOptions array
  const currentIndex = timeframeOptions.indexOf(form.mainTimeframe);
  if (currentIndex === -1) {
    return {
      recommendation: "טיימפריים ראשי לא תקין",
      phasesWithC: [],
      targetTimeframe: null,
      timeframesToAskR: [],
      targetFiboLevels: []
    };
  }
  
  const phasesWithC = [];
  // Add main timeframe if it's C, D or E
  if (['C', 'D', 'E'].includes(form.phases[form.mainTimeframe])) {
    phasesWithC.push(form.mainTimeframe);
  }
  
  let targetTimeframe = form.mainTimeframe;
  
  // Check higher timeframes
  for (let i = currentIndex + 1; i < timeframeOptions.length; i++) {
    const tf = timeframeOptions[i];
    // If phase is C, D or E - add and continue
    if (form.phases[tf] === 'C' || form.phases[tf] === 'D' || form.phases[tf] === 'E') {
      phasesWithC.push(tf);
      targetTimeframe = tf;
    } else {
      // Otherwise, exit loop and use this timeframe as target
      targetTimeframe = tf;
      break;
    }
  }
  
  // Determine timeframes to ask for R values
  let timeframesToAskR = [];
  const startIndex = currentIndex;
  const targetIndex = timeframeOptions.indexOf(targetTimeframe);
  
  for (let i = startIndex; i <= targetIndex && i < timeframeOptions.length; i++) {
    timeframesToAskR.push(timeframeOptions[i]);
  }
  
  // Set recommendation based on trade direction
  const target = form.tradeDirection === 'long' ? 'ICE/CREEK' : 'ICE/CREEK';
  let recommendation = "";
  
  if (phasesWithC.length === 0) {
    recommendation = `יעד TP: ${target} בטיימפריים ${targetTimeframe} | יעדי פיבונאצ'י: ${targetFiboLevels.join(', ')}`;
  } else if (phasesWithC.length === 1) {
    recommendation = `יעד TP: ${target} בטיימפריים ${targetTimeframe} | יעדי פיבונאצ'י: ${targetFiboLevels.join(', ')}`;
  } else {
    recommendation = `יעד TP: ${target} בטיימפריים ${targetTimeframe} (דרך פאזות C ב: ${phasesWithC.join(', ')}) | יעדי פיבונאצ'י: ${targetFiboLevels.join(', ')}`;
  }
  
  return {
    recommendation,
    phasesWithC,
    targetTimeframe,
    timeframesToAskR,
    targetFiboLevels
  };
}

const TradeForm = ({ user, userProfile, setShowTradeForm, showErrorMessage, setSnackbarMessage, setSnackbarOpen, setIsSaving }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for form fields
  const [tradeForm, setTradeForm] = useState(initialTradeForm);
  const [formStep, setFormStep] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [missingFields, setMissingFields] = useState([]);
  const [missingFieldsDialogOpen, setMissingFieldsDialogOpen] = useState(false);
  const [stopReasonDialogOpen, setStopReasonDialogOpen] = useState(false);
  const [stopReasonValue, setStopReasonValue] = useState('');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [newReport, setNewReport] = useState({ report: 'אין דיווח', time: '' });
  const [rValueDialogOpen, setRValueDialogOpen] = useState(false);
  const [currentRValueTimeframe, setCurrentRValueTimeframe] = useState('');
  const [currentRValue, setCurrentRValue] = useState('');
  const [currentRValueType, setCurrentRValueType] = useState(''); // 'normal' or 'fibo'
  const [currentFiboLevel, setCurrentFiboLevel] = useState(''); // for fibo R values
  const [tpResult, setTpResult] = useState({
    recommendation: '',
    phasesWithC: [],
    targetTimeframe: '',
    timeframesToAskR: [],
    targetFiboLevels: []
  });
  
  // New states for another trigger dialog
  const [anotherTriggerDialogOpen, setAnotherTriggerDialogOpen] = useState(false);
  const [anotherTriggerType, setAnotherTriggerType] = useState('');
  const [anotherTriggerStep, setAnotherTriggerStep] = useState(0); // 0: ask yes/no, 1: select trigger

  // Effect to autofill trigger from user profile if available
  useEffect(() => {
    if (userProfile?.trigger && tradeForm.matchedTrigger === '') {
      setTradeForm((prev) => ({ ...prev, matchedTrigger: userProfile.trigger }));
    }
  }, [userProfile?.trigger, tradeForm.matchedTrigger]);
  
  // Effect to calculate TP recommendation when phases, timeframes, or Fibonacci levels change
  useEffect(() => {
    if (tradeForm.triggerAppeared === 'yes') {
      const result = calculateTPRecommendation(tradeForm);
      setTpResult(result);
      
      setTradeForm(prev => ({
        ...prev,
        tpRecommendation: result.recommendation,
        phasesWithC: result.phasesWithC || []
      }));
    }
  }, [tradeForm.phases, tradeForm.mainTimeframe, tradeForm.tradeDirection, tradeForm.entryFiboLevel]);
  
  // Effect to automatically calculate trade duration
  useEffect(() => {
    if (tradeForm.date && tradeForm.time && tradeForm.closeDate && tradeForm.closeTime) {
      const duration = calculateTradeDuration(
        tradeForm.date,
        tradeForm.time,
        tradeForm.closeDate,
        tradeForm.closeTime
      );
      
      if (duration) {
        setTradeForm(prev => ({
          ...prev,
          duration
        }));
      }
    }
  }, [tradeForm.date, tradeForm.time, tradeForm.closeDate, tradeForm.closeTime]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setFormStep(newValue);
  };

  // R value dialog functions
  const openRValueDialog = (timeframe, type = 'normal', fiboLevel = '') => {
    setCurrentRValueTimeframe(timeframe);
    setCurrentRValueType(type);
    setCurrentFiboLevel(fiboLevel);
    
    if (type === 'normal') {
      setCurrentRValue(tradeForm.achievedRValues[timeframe] || '');
    } else {
      // For fibo values, use the specific fibo level
      if (fiboLevel === 'max') {
        setCurrentRValue(tradeForm.fiboRValues.max || '');
      } else {
        setCurrentRValue(tradeForm.fiboRValues[fiboLevel] || '');
      }
    }
    setRValueDialogOpen(true);
  };
  
  const handleRValueChange = (e) => {
    setCurrentRValue(e.target.value);
  };
  
  const handleSaveRValue = () => {
    if (currentRValueType === 'normal') {
      setTradeForm(prev => ({
        ...prev,
        achievedRValues: {
          ...prev.achievedRValues,
          [currentRValueTimeframe]: currentRValue
        }
      }));
    } else {
      // Save fibo R value
      setTradeForm(prev => ({
        ...prev,
        fiboRValues: {
          ...prev.fiboRValues,
          [currentFiboLevel]: currentRValue
        }
      }));
    }
    setRValueDialogOpen(false);
  };
  
  // Form change handlers
  const handleTradeFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'outcome') {
      if (value === 'Stop Loss') {
        setStopReasonDialogOpen(true);
        setTradeForm((prev) => ({ ...prev, outcome: value, tpMilestoneExit: "-1R", finalTP: [] }));
      } else if (value === 'Break Even') {
        setStopReasonValue('');
        setTradeForm((prev) => ({ ...prev, outcome: value, tpMilestoneExit: "1:0", finalTP: [] }));
      } else {
        setStopReasonValue('');
        setTradeForm((prev) => ({ ...prev, outcome: value }));
      }
      return;
    }
    if (name === 'triggerAppeared') {
      if (value === 'no') {
        setTradeForm((prev) => ({
          ...prev,
          triggerAppeared: 'no',
          // Clear only trigger-related fields, keep reports and images
          matchedTrigger: '',
          outcome: '',
          stopPercentage: '',
          duration: '',
          tpMilestonesReached: [],
          tpMilestoneExit: '',
          stopReason: '',
          finalTP: [],
          phases: {
            '3m': '',
            '5m': '',
            '15m': '',
            '1h': '',
            '4h': '',
            '1d': '',
            '1w': '',
          },
          mainTimeframe: '15m',
          tradeDirection: 'long',
          tpRecommendation: '',
          phasesWithC: [],
          firstSpikeCandle: '',
          internalPivotCandle: '',
          secondSpikeCandle: '',
          bossConfirmationCandle: '',
          entryFiboLevel: '',
          startFiboLevel: '',
          reactionFiboLevels: [],
          targetFiboLevel: [],
          maxFiboLevel: '',
          achievedRValues: {},
          fiboRValues: {
            '1.414': '',
            '1.618': '',
            '2.236': '',
            '2.618': '',
            'max': ''
          }
          // Note: reports, linkBefore, linkAfter are preserved
        }));
      } else {
        setTradeForm((prev) => ({ ...prev, triggerAppeared: 'yes' }));
      }
      return;
    }
    
    // Handle date changes
    if (name === 'date') {
      if (!value) {
        setTradeForm((prev) => ({ ...prev, date: '', dayOfWeek: '' }));
      } else {
        const jsDate = new Date(value);
        const dayIndex = jsDate.getDay();
        const dayName = dayOptions[dayIndex];
        setTradeForm((prev) => ({
          ...prev,
          date: value,
          dayOfWeek: dayName,
        }));
      }
      return;
    }
    
    // Handle main timeframe change
    if (name === 'mainTimeframe') {
      setTradeForm((prev) => ({ ...prev, mainTimeframe: value }));
      return;
    }
    
    // Handle trade direction change
    if (name === 'tradeDirection') {
      setTradeForm((prev) => ({ ...prev, tradeDirection: value }));
      return;
    }
    
    setTradeForm((prev) => ({ ...prev, [name]: value }));
  };
  
  // Handle phase change per timeframe
  const handlePhaseChange = (timeframe, phase) => {
    setTradeForm((prev) => ({
      ...prev,
      phases: {
        ...prev.phases,
        [timeframe]: phase
      }
    }));
  };

  // Handle Fibonacci level changes
  const handleFiboChange = (e) => {
    const { name, value } = e.target;
    setTradeForm((prev) => ({ ...prev, [name]: value }));
  };
  
  const handleReactionFiboChange = (event, newValue) => {
    setTradeForm((prev) => ({ ...prev, reactionFiboLevels: newValue }));
  };

  const handleTargetFiboChange = (event, newValue) => {
    setTradeForm((prev) => ({ ...prev, targetFiboLevel: newValue }));
  };

  // Date change handlers


  // TP milestones handlers
  const handleTradeFormMilestonesReachedChange = (event, newValue) => {
    setTradeForm((prev) => ({ ...prev, tpMilestonesReached: newValue }));
  };

  const handleTradeFormMilestoneExitChange = (e) => {
    setTradeForm((prev) => ({ ...prev, tpMilestoneExit: e.target.value }));
  };

  const handleFinalTPChange = (event, newValue) => {
    setTradeForm((prev) => ({ ...prev, finalTP: newValue }));
  };

  // Reports dialog handlers
  const handleOpenReportDialog = () => {
    setNewReport({ report: 'אין דיווח', time: '' });
    setReportDialogOpen(true);
  };
  
  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
  };
  
  const handleAddReport = () => {
    if (!newReport.report || !newReport.time) {
      showErrorMessage("נא למלא את כל שדות הדיווח");
      return;
    }
    
    setTradeForm((prev) => ({ 
      ...prev, 
      reports: [...prev.reports, newReport] 
    }));
    
    setReportDialogOpen(false);
  };
  
  const handleRemoveReport = (index) => {
    setTradeForm((prev) => ({
      ...prev,
      reports: prev.reports.filter((_, i) => i !== index)
    }));
  };
  
  const handleReportChange = (e) => {
    setNewReport({
      ...newReport,
      [e.target.name]: e.target.value
    });
  };
  

  
  // Form navigation functions
  const handleNextStep = () => {
    setFormStep(prev => prev + 1);
    setActiveTab(prev => prev + 1);
  };
  
  const handlePrevStep = () => {
    setFormStep(prev => Math.max(0, prev - 1));
    setActiveTab(prev => Math.max(0, prev - 1));
  };

  // Form submission handler
  const handleTradeFormSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    const missing = getMissingFields(tradeForm);
    if (missing.length > 0) {
      setMissingFields(missing);
      setMissingFieldsDialogOpen(true);
      return;
    } else {
      await recordTrade();
    }
  };

  // Record trade in Firestore
  const recordTrade = async (showAnotherTriggerDialog = true) => {
    try {
      // Show loading indicator
      setIsSaving(true);
      console.log("מתחיל שמירת עסקה...");
      
      // Create a clean copy of the form object and prepare it for storage
      let cleanTradeForm = {...tradeForm};
      
      // Delete fields that shouldn't be saved to Firestore
      const fieldsToDelete = ['phasesWithC'];
      fieldsToDelete.forEach(field => {
        delete cleanTradeForm[field];
      });
      
      // Clean and secure values
      if (!cleanTradeForm.phases) cleanTradeForm.phases = {};
      if (!cleanTradeForm.achievedRValues) cleanTradeForm.achievedRValues = {};
      if (!cleanTradeForm.fiboRValues) cleanTradeForm.fiboRValues = {
        '1.414': '',
        '1.618': '',
        '2.236': '',
        '2.618': '',
        'max': ''
      };
      if (!cleanTradeForm.reports) cleanTradeForm.reports = [];
      if (!cleanTradeForm.tpMilestonesReached) cleanTradeForm.tpMilestonesReached = [];
      if (!cleanTradeForm.finalTP) cleanTradeForm.finalTP = [];
      if (!cleanTradeForm.reactionFiboLevels) cleanTradeForm.reactionFiboLevels = [];
      
      // Convert numerical values
      cleanTradeForm.duration = cleanTradeForm.duration ? Number(cleanTradeForm.duration) || 0 : 0;
      cleanTradeForm.stopPercentage = cleanTradeForm.stopPercentage ? Number(cleanTradeForm.stopPercentage) || 0 : 0;
      
      // Convert R values to numbers
      const safeAchievedRValues = {};
      Object.keys(cleanTradeForm.achievedRValues || {}).forEach(key => {
        const value = cleanTradeForm.achievedRValues[key];
        safeAchievedRValues[key] = value ? Number(value) || 0 : 0;
      });
      cleanTradeForm.achievedRValues = safeAchievedRValues;
      
      // Convert Fibo R values to numbers
      const safeFiboRValues = {};
      Object.keys(cleanTradeForm.fiboRValues || {}).forEach(key => {
        const value = cleanTradeForm.fiboRValues[key];
        safeFiboRValues[key] = value ? Number(value) || 0 : 0;
      });
      cleanTradeForm.fiboRValues = safeFiboRValues;
      
      // Step 1: Save basic info first
      const basicInfo = {
        date: cleanTradeForm.date || '',
        dayOfWeek: cleanTradeForm.dayOfWeek || '',
        time: cleanTradeForm.time || '',
        triggerAppeared: cleanTradeForm.triggerAppeared || 'no',
        symbol: cleanTradeForm.symbol || 'BTC',
        uid: user.uid,
        userName: user.displayName || user.email,
        createdAt: new Date()
      };
      
      console.log("שומר מידע בסיסי...");
      let docRef;
      try {
        docRef = await addDoc(collection(db, 'trades'), basicInfo);
      } catch (error) {
        console.error("שגיאה בשמירת נתונים בסיסיים:", error);
        throw new Error(`שגיאה בשמירת נתונים בסיסיים: ${error.message}`);
      }
      
      // Step 2: Update with additional data (reports and images always, trigger data only if trigger exists)
      console.log("מעדכן נתונים נוספים...");
      
      // Combine reports for backward compatibility
      const legacyReport = cleanTradeForm.reports && cleanTradeForm.reports.length > 0 
        ? cleanTradeForm.reports[0].report 
        : 'אין דיווח';
      
      const additionalData = {
        // Reports and images - always saved
        report: legacyReport,
        reports: cleanTradeForm.reports || [],
        linkBefore: cleanTradeForm.linkBefore || '',
        linkAfter: cleanTradeForm.linkAfter || '',
        
        // Update timestamp
        updatedAt: new Date()
      };
      
      // Add trigger-related data only if there's a trigger
      if (cleanTradeForm.triggerAppeared === 'yes') {
        // Create milestones
        const milestones = (cleanTradeForm.tpMilestonesReached || []).map((tp) => ({
          tp,
          status: 'הושג',
        }));
        
        // Handle exit milestone
        if (cleanTradeForm.tpMilestoneExit && cleanTradeForm.outcome !== 'Stop Loss') {
          milestones.push({ tp: cleanTradeForm.tpMilestoneExit, status: 'יציאה' });
        } else if(cleanTradeForm.outcome === 'Stop Loss'){
          milestones.push({ tp: "-1R", status: 'יציאה' });
        } else if(cleanTradeForm.outcome === 'Break Even'){
          milestones.push({ tp: "1:0", status: 'יציאה' });
        }
        
        Object.assign(additionalData, {
          matchedTrigger: cleanTradeForm.matchedTrigger || '',
          session: cleanTradeForm.session || '',
          stopPercentage: Number(cleanTradeForm.stopPercentage) || 0,
          outcome: cleanTradeForm.outcome || '',
          duration: Number(cleanTradeForm.duration) || 0,
          
          // Additional data
          tpMilestones: milestones,
          stopReason: stopReasonValue || '',
          finalTP: cleanTradeForm.finalTP || [],
          
          // Advanced fields
          mainTimeframe: cleanTradeForm.mainTimeframe || '15m',
          tradeDirection: cleanTradeForm.tradeDirection || 'long',
          phases: cleanTradeForm.phases || {},
          achievedRValues: cleanTradeForm.achievedRValues || {},
          fiboRValues: cleanTradeForm.fiboRValues || {},
          tpRecommendation: cleanTradeForm.tpRecommendation || '',
          
          // Fibonacci fields
          entryFiboLevel: cleanTradeForm.entryFiboLevel || '',
          startFiboLevel: cleanTradeForm.startFiboLevel || '',
          reactionFiboLevels: cleanTradeForm.reactionFiboLevels || [],
          targetFiboLevel: cleanTradeForm.targetFiboLevel || [],
          maxFiboLevel: cleanTradeForm.maxFiboLevel || '',
          
          // Candle times
          firstSpikeCandle: cleanTradeForm.firstSpikeCandle || '',
          internalPivotCandle: cleanTradeForm.internalPivotCandle || '',
          secondSpikeCandle: cleanTradeForm.secondSpikeCandle || '',
          bossConfirmationCandle: cleanTradeForm.bossConfirmationCandle || '',
          
          // Closing data
          closeDate: cleanTradeForm.closeDate || '',
          closeTime: cleanTradeForm.closeTime || '',
        });
      }
      
      try {
        await updateDoc(docRef, additionalData);
      } catch (error) {
        console.error("שגיאה בעדכון נתונים נוספים:", error);
        throw new Error(`שגיאה בעדכון נתונים: ${error.message}`);
      }
      
      console.log("העסקה נשמרה בהצלחה!");
      
      // Show success message
      setSnackbarMessage("העסקה נשמרה בהצלחה");
      setSnackbarOpen(true);
      
      // Ask about another trigger on same day
      if (showAnotherTriggerDialog && cleanTradeForm.triggerAppeared === 'yes') {
        setAnotherTriggerStep(0); // Reset to first step
        setAnotherTriggerDialogOpen(true);
      } else {
        // Reset form and close
        setTradeForm(initialTradeForm);
        setStopReasonValue('');
        setShowTradeForm(false);
        setFormStep(0);
        setActiveTab(0);
      }
    } catch (err) {
      console.error('שגיאה בהוספת עסקה:', err);
      showErrorMessage(`שגיאה בשמירה: ${err.message}`);
    } finally {
      // Always turn off saving indicator
      setIsSaving(false);
    }
  };

  // Handle another trigger dialog
  const handleAnotherTriggerResponse = async (hasAnother) => {
    if (!hasAnother) {
      // Reset form and close
      setTradeForm(initialTradeForm);
      setStopReasonValue('');
      setShowTradeForm(false);
      setFormStep(0);
      setActiveTab(0);
      setAnotherTriggerDialogOpen(false);
      setAnotherTriggerStep(0);
      return;
    }
    
    // User has another trigger - move to next step
    setAnotherTriggerStep(1);
  };
  
  const handleAnotherTriggerConfirm = () => {
    // Save current trade data
    const savedData = {
      date: tradeForm.date,
      dayOfWeek: tradeForm.dayOfWeek,
      symbol: tradeForm.symbol,
      reports: tradeForm.reports,
      phases: { ...tradeForm.phases },
      mainTimeframe: tradeForm.mainTimeframe,
      tradeDirection: tradeForm.tradeDirection,
      // Add other fields that should be preserved
    };
    
    // Reset form with saved data
    const newForm = {
      ...initialTradeForm,
      ...savedData,
      triggerAppeared: 'yes',
      matchedTrigger: anotherTriggerType || userProfile?.trigger || '',
      session: '',
    };
    
    setTradeForm(newForm);
    setAnotherTriggerDialogOpen(false);
    setAnotherTriggerType('');
    setAnotherTriggerStep(0);
    setFormStep(0);
    setActiveTab(0);
  };

  // Dialog handlers
  const handleMissingFieldsRecord = async () => {
    setMissingFieldsDialogOpen(false);
    await recordTrade();
  };
  
  const handleMissingFieldsBack = () => {
    setMissingFieldsDialogOpen(false);
  };

  const handleCloseStopReasonDialog = () => {
    setStopReasonDialogOpen(false);
  };
  
  const handleSaveStopReason = () => {
    setStopReasonDialogOpen(false);
  };

  // Get candle timeframe label based on main timeframe
  const getCandleTimeframeLabel = () => {
    if (tradeForm.mainTimeframe) {
      return `זמני נרות ספציפיים (${tradeForm.mainTimeframe})`;
    }
    return 'זמני נרות ספציפיים';
  };

  // Styling
  const styles = {
    formContainer: {
      borderRadius: 1,
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      direction: 'rtl',
      textAlign: 'right'
    },
    stepperContainer: {
      backgroundColor: alpha(theme.palette.background.paper, 0.6),
      borderRadius: 1,
      position: 'relative',
      marginTop: 8,
      '& .MuiStepLabel-root': {
        '& .MuiStepIcon-root': {
          fontSize: 26,
          '&.Mui-active': {
            color: theme.palette.primary.main,
          },
          '&.Mui-completed': {
            color: theme.palette.success.main,
          },
        },
        '& .MuiStepLabel-label': {
          marginTop: 4,
          fontSize: '0.9rem',
          '&.Mui-active': {
            color: theme.palette.primary.main,
            fontWeight: 'bold'
          },
        },
      },
    },
    tabsContainer: {
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
      borderRadius: 4,
      marginBottom: 2,
      '& .MuiTabs-indicator': {
        backgroundColor: theme.palette.primary.main,
        height: 3,
      },
      '& .MuiTab-root': {
        minHeight: 48,
        fontWeight: 600,
        fontSize: '0.9rem',
        textTransform: 'none',
        '&.Mui-selected': {
          color: theme.palette.primary.main,
        },
      },
    },
    formSection: {
      padding: 2,
      borderRadius: 1,
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
      boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
      marginBottom: 24,
      border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      position: 'relative',
    },
    formHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 20,
      padding: '0 0 12px 0',
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    },
    formHeaderIcon: {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
      borderRadius: 1,
      padding: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 10,
      '& svg': {
        fontSize: '1.1rem',
      }
    },
    phaseGrid: {
      marginTop: 16,
      marginBottom: 24,
      padding: 2,
      backgroundColor: alpha(theme.palette.background.default, 0.4),
      borderRadius: 1,
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },
    phaseGridHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 16,
    },
    phaseCard: {
      padding: 1.5,
      marginBottom: 2,
      borderRadius: 1,
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      '&:hover': {
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        borderColor: alpha(theme.palette.primary.main, 0.3),
      },
      transition: 'all 0.2s ease',
    },
    phaseLabel: {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
      marginBottom: 8,
      display: 'flex',
      alignItems: 'center'
    },
    enhancedTPBox: {
      background: `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.15)} 100%)`,
      borderRadius: 1,
      padding: '20px 24px',
      boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
      position: 'relative',
      overflow: 'hidden',
      marginTop: 20,
      marginBottom: 20,
    },
    fiboBox: {
      background: `linear-gradient(135deg, ${alpha(theme.palette.info.light, 0.08)} 0%, ${alpha(theme.palette.info.main, 0.15)} 100%)`,
      borderRadius: 1,
      padding: '20px 24px',
      boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
      position: 'relative',
      overflow: 'hidden',
      marginTop: 20,
      marginBottom: 20,
    },
    tpIcon: {
      position: 'absolute',
      left: 20,
      top: 20,
      opacity: 0.15,
      color: theme.palette.success.main,
      fontSize: '7rem',
      transform: 'rotate(15deg)'
    },
    fiboIcon: {
      position: 'absolute',
      left: 20,
      top: 20,
      opacity: 0.15,
      color: theme.palette.info.main,
      fontSize: '7rem',
      transform: 'rotate(15deg)'
    },
    tpTitle: {
      fontWeight: 700,
      fontSize: '1.1rem',
      color: theme.palette.success.main,
      marginBottom: 12,
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      alignItems: 'center'
    },
    fiboTitle: {
      fontWeight: 700,
      fontSize: '1.1rem',
      color: theme.palette.info.main,
      marginBottom: 12,
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      alignItems: 'center'
    },
    tpValue: {
      fontSize: '1.2rem',
      fontWeight: 600,
      color: theme.palette.success.main,
      position: 'relative',
      zIndex: 1,
      padding: '8px 16px',
      backgroundColor: alpha(theme.palette.success.main, 0.08),
      borderRadius: 1,
      display: 'inline-block',
      border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
    },
    fiboValue: {
      fontSize: '1.2rem',
      fontWeight: 600,
      color: theme.palette.info.main,
      position: 'relative',
      zIndex: 1,
      padding: '8px 16px',
      backgroundColor: alpha(theme.palette.info.main, 0.08),
      borderRadius: 1,
      display: 'inline-block',
      border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
    },
    tpDetails: {
      fontSize: '0.9rem',
      color: theme.palette.text.secondary,
      marginTop: 12,
      position: 'relative',
      zIndex: 1,
      padding: '8px 0',
    },
    rValueSection: {
      marginTop: 16,
      padding: 10,
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
      borderRadius: 1,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      position: 'relative'
    },
    rValueTitle: {
      fontWeight: 700,
      marginBottom: 2,
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.primary.main,
      fontSize: '1.05rem'
    },
    rValueCard: {
      padding: 12,
      borderRadius: 12,
      backgroundColor: alpha(theme.palette.background.paper, 0.7),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      marginBottom: 12,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'all 0.25s ease',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
      }
    },
    rValueFilled: {
      backgroundColor: alpha(theme.palette.success.main, 0.08),
      border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`
    },
    formStepButtons: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: 24
    },
    formNavigationButton: {
      borderRadius: 12,
      padding: '10px 24px',
      fontWeight: 600,
      fontSize: '0.95rem',
      boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
      height: 48,
      minWidth: 100,
      '&:hover': {
        boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
      }
    },
    saveButton: {
      borderRadius: 12,
      padding: '10px 28px',
      fontWeight: 600,
      fontSize: '1rem',
      boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
      height: 48,
      minWidth: 140,
      transition: 'all 0.3s',
      '&:hover': {
        boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
        transform: 'translateY(-2px)'
      }
    },
    reportList: {
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      borderRadius: 8,
      padding: 0,
      marginTop: 8,
      maxHeight: 200,
      overflow: 'auto',
      backgroundColor: alpha(theme.palette.background.default, 0.3),
    },
    reportItem: {
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      padding: '8px 16px',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
      }
    },
    inputField: {
      '& .MuiInputBase-root': {
        borderRadius: 10,
        backgroundColor: alpha(theme.palette.background.default, 0.3),
        transition: 'all 0.2s',
        height: 48,
        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        '&:hover': {
          backgroundColor: alpha(theme.palette.background.default, 0.5),
          boxShadow: '0 4px 8px rgba(0,0,0,0.06)',
          borderColor: alpha(theme.palette.primary.main, 0.3),
        },
        '&.Mui-focused': {
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
          borderColor: theme.palette.primary.main,
        }
      },
    },
    imageSection: {
      marginTop: 16,
      padding: 16,
      borderRadius: 12,
      backgroundColor: alpha(theme.palette.background.default, 0.3),
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    },
    candleTimesContainer: {
      padding: 16,
      marginTop: 16,
      backgroundColor: alpha(theme.palette.info.main, 0.05),
      borderRadius: 12,
      border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
      position: 'relative'
    },
    summaryBox: {
      padding: 24,
      borderRadius: 12,
      backgroundColor: alpha(theme.palette.info.main, 0.05),
      border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
      marginBottom: 24,
    }
  };

  // Define steps based on the new structure
  const steps = [
    { label: 'פרטים בסיסיים', icon: <TuneIcon /> },
    { label: 'פאזות וטיימפריימים', icon: <BarChartIcon /> },
    { label: 'כניסה', icon: <CallMadeIcon /> },
    { label: 'יציאה', icon: <CallReceivedIcon /> },
    { label: 'סיכום', icon: <CheckBoxOutlinedIcon /> }
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper
        component="form"
        onSubmit={handleTradeFormSubmit}
        sx={styles.formContainer}
      >
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider', 
          py: 2, 
          px: 3,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
        }}>
          <IconButton 
            size="small" 
            onClick={() => setShowTradeForm(false)}
            sx={{ 
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              '&:hover': {
                bgcolor: alpha(theme.palette.error.main, 0.2),
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              הזנת נתוני עסקה
            </Typography>
            <Avatar 
              sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.1), 
                color: theme.palette.primary.main,
                width: 38,
                height: 38,
                mr: 2,
                ml: 2
              }}
            >
              <AddCircleOutlineIcon />
            </Avatar>
          </Box>
        </Box>

        {/* Tabs for easy navigation */}
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          sx={styles.tabsContainer}
        >
          {steps.map((step, index) => (
            <Tab 
              key={index} 
              label={step.label} 
              icon={step.icon} 
              iconPosition="start"
              disabled={tradeForm.triggerAppeared === 'no' && index > 0 && index < 4}
            />
          ))}
        </Tabs>

        {/* Traditional stepper (optional, can be removed if tabs are sufficient) */}
        <Box sx={styles.stepperContainer}>
          <Stepper activeStep={formStep} alternativeLabel connector={<StepConnector sx={{ display: 'none' }} />}>
            {steps.map((step, index) => (
              <Step key={index}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step 1: Basic Details */}
        {formStep === 0 && (
          <Box sx={{ p: 3 }}>
            {/* Basic Details */}
            <Box sx={styles.formSection}>
              <Box sx={styles.formHeader}>
                <Typography variant="subtitle1" fontWeight="bold">
                  פרטים בסיסיים
                </Typography>
                <Box sx={styles.formHeaderIcon}>
                  <TuneIcon />
                </Box>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                    <InputLabel>סימול</InputLabel>
                    <Select
                      name="symbol"
                      value={tradeForm.symbol}
                      onChange={handleTradeFormChange}
                      label="סימול"
                    >
                      {symbolOptions.map((sym) => (
                        <MenuItem key={sym} value={sym}>
                          {sym}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="תאריך פתיחה"
                    type="date"
                    name="date"
                    value={tradeForm.date}
                    onChange={handleTradeFormChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={styles.inputField}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="שעת פתיחה"
                    type="time"
                    name="time"
                    value={tradeForm.time}
                    onChange={handleTradeFormChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={styles.inputField}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                    <InputLabel>יום בשבוע</InputLabel>
                    <Select
                      name="dayOfWeek" 
                      value={tradeForm.dayOfWeek}
                      onChange={handleTradeFormChange}
                      label="יום בשבוע"
                    >
                      {dayOptions.map((day) => (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Closing data */}
                <Grid item xs={12} md={3}>
                  <TextField
                    label="תאריך סגירה"
                    type="date"
                    name="closeDate"
                    value={tradeForm.closeDate}
                    onChange={handleTradeFormChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={styles.inputField}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="שעת סגירה"
                    type="time"
                    name="closeTime"
                    value={tradeForm.closeTime}
                    onChange={handleTradeFormChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={styles.inputField}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="משך זמן (דקות) - מחושב אוטומטית"
                    name="duration"
                    type="number"
                    value={tradeForm.duration}
                    onChange={handleTradeFormChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={styles.inputField}
                    InputProps={{
                      readOnly: !!(tradeForm.closeDate && tradeForm.closeTime),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                    <InputLabel>האם היה טריגר?</InputLabel>
                    <Select
                      name="triggerAppeared"
                      value={tradeForm.triggerAppeared}
                      onChange={handleTradeFormChange}
                      label="האם היה טריגר?"
                    >
                      <MenuItem value="no">לא</MenuItem>
                      <MenuItem value="yes">כן</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Trigger details */}
            {tradeForm.triggerAppeared === 'yes' && (
              <Box sx={styles.formSection}>
                <Box sx={styles.formHeader}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    פרטי טריגר
                  </Typography>
                  <Box sx={styles.formHeaderIcon}>
                    <ApartmentIcon />
                  </Box>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                      <InputLabel>טריגר תואם</InputLabel>
                      <Select
                        name="matchedTrigger"
                        value={tradeForm.matchedTrigger}
                        onChange={handleTradeFormChange}
                        label="טריגר תואם"
                      >
                        {triggerOptions.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                      <InputLabel>סשן</InputLabel>
                      <Select
                        name="session"
                        value={tradeForm.session}
                        onChange={handleTradeFormChange}
                        label="סשן"
                      >
                        {sessionOptions.map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Reports section */}
            <Box sx={styles.formSection}>
              <Box sx={styles.formHeader}>
                <Typography variant="subtitle1" fontWeight="bold">
                  דיווחים
                </Typography>
                <Box sx={styles.formHeaderIcon}>
                  <EventNoteIcon />
                </Box>
              </Box>
              
              {tradeForm.reports && tradeForm.reports.length > 0 ? (
                <List sx={{
                  ...styles.reportList,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  borderRadius: 2,
                  maxHeight: 200,
                  marginBottom: 2
                }}>
                  {tradeForm.reports.map((reportItem, index) => (
                    <ListItem key={index} sx={styles.reportItem}>
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          onClick={() => handleRemoveReport(index)} 
                          size="small"
                          sx={{
                            color: theme.palette.error.main,
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            '&:hover': {
                              bgcolor: alpha(theme.palette.error.main, 0.2),
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                      <ListItemText 
                        primary={reportItem.report} 
                        secondary={`בשעה ${reportItem.time}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                  לא הוגדרו דיווחים
                </Typography>
              )}
              
              <Button
                startIcon={<AddIcon />}
                fullWidth
                variant="outlined"
                size="small"
                onClick={handleOpenReportDialog}
                sx={{
                  borderRadius: 8,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  height: 42,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  }
                }}
              >
                הוסף דיווח
              </Button>
            </Box>

            {/* Chart images */}
            <Box sx={styles.imageSection}>
              <Box sx={styles.formHeader}>
                <Typography variant="subtitle1" fontWeight="bold">
                  תמונות גרף
                </Typography>
                <Box sx={styles.formHeaderIcon}>
                  <ImageIcon />
                </Box>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="תמונה לפני (URL)"
                    name="linkBefore"
                    value={tradeForm.linkBefore}
                    onChange={handleTradeFormChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    required
                    sx={styles.inputField}
                  />
                </Grid>
                
                {tradeForm.triggerAppeared === 'yes' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="תמונה אחרי (URL)"
                      name="linkAfter"
                      value={tradeForm.linkAfter}
                      onChange={handleTradeFormChange}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={styles.inputField}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            <Box sx={styles.formStepButtons}>
              {/* If no trigger, show "Record Trade" button */}
              {tradeForm.triggerAppeared === 'no' ? (
                <Button 
                  type="submit"
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  sx={{
                    ...styles.saveButton,
                    mr: 'auto'
                  }}
                  disabled={!tradeForm.date || !tradeForm.time || !tradeForm.symbol || !tradeForm.linkBefore}
                >
                  שמור עסקה
                </Button>
              ) : (
                <>
                  <Button 
                    variant="contained"
                    color="primary"
                    startIcon={<NavigateNextIcon />}
                    onClick={handleNextStep}
                    disabled={!tradeForm.date || !tradeForm.time || !tradeForm.symbol}
                    sx={styles.formNavigationButton}
                  >
                    המשך לשלב הבא
                  </Button>
                  <div></div> {/* Spacer for layout */}
                </>
              )}
            </Box>
          </Box>
        )}

        {/* Step 2: Phases & Timeframes */}
        {formStep === 1 && tradeForm.triggerAppeared === 'yes' && (
          <Box sx={{ p: 3 }}>
            <Box sx={styles.formSection}>
              <Box sx={styles.formHeader}>
                <Typography variant="subtitle1" fontWeight="bold">
                  פאזות וטיימפריימים
                </Typography>
                <Box sx={styles.formHeaderIcon}>
                  <BarChartIcon />
                </Box>
              </Box>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                הגדר את הפאזה עבור כל טיימפריים רלוונטי. המערכת תחשב את יעד ה-TP האופטימלי.
              </Typography>
              
              {/* Trade Direction */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 'bold', 
                  color: theme.palette.primary.main,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center' 
                }}>
                  <TrendingUpIcon fontSize="small" sx={{ ml: 1 }} />
                  כיוון עסקה
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    name="tradeDirection"
                    value={tradeForm.tradeDirection}
                    onChange={handleTradeFormChange}
                  >
                    <FormControlLabel 
                      value="long" 
                      control={
                        <Radio 
                          color="success"
                          sx={{ 
                            '& .MuiSvgIcon-root': { fontSize: 22 } 
                          }}
                        />
                      } 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TrendingUpIcon color="success" sx={{ ml: 0.5 }} />
                          <Typography>לונג</Typography>
                        </Box>
                      } 
                    />
                    <FormControlLabel 
                      value="short" 
                      control={
                        <Radio 
                          color="error"
                          sx={{ 
                            '& .MuiSvgIcon-root': { fontSize: 22 } 
                          }}
                        />
                      } 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TrendingDownIcon color="error" sx={{ ml: 0.5 }} />
                          <Typography>שורט</Typography>
                        </Box>
                      } 
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
              
              {/* Main Timeframe */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 'bold', 
                  color: theme.palette.primary.main,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center' 
                }}>
                  <TimelineIcon fontSize="small" sx={{ ml: 1 }} />
                  טיימפריים ראשי
                </Typography>
                <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                  <InputLabel>טיימפריים ראשי</InputLabel>
                  <Select
                    name="mainTimeframe"
                    value={tradeForm.mainTimeframe}
                    onChange={handleTradeFormChange}
                    label="טיימפריים ראשי"
                  >
                    {timeframeOptions.map((tf) => (
                      <MenuItem key={tf} value={tf}>
                        {tf}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              {/* Phase grid */}
              <Box sx={styles.phaseGrid}>
                <Box sx={styles.phaseGridHeader}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    borderRadius: 8,
                    padding: '8px',
                    marginLeft: 10
                  }}>
                    <TuneIcon />
                  </Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    הגדרת פאזות:
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  {timeframeOptions.map((tf) => (
                    <Grid item xs={12} sm={6} md={4} key={tf}>
                      <Paper 
                        sx={{
                          ...styles.phaseCard,
                          ...(tf === tradeForm.mainTimeframe ? {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 2,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          } : {})
                        }}
                      >
                        <Typography variant="subtitle2" sx={styles.phaseLabel}>
                          {tf}
                          {tf === tradeForm.mainTimeframe && (
                            <Chip 
                              label="ראשי" 
                              size="small" 
                              color="primary" 
                              sx={{ mr: 1, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Typography>
                        <FormControl fullWidth size="small" sx={styles.inputField}>
                          <InputLabel>פאזה</InputLabel>
                          <Select
                            value={tradeForm.phases[tf]}
                            onChange={(e) => handlePhaseChange(tf, e.target.value)}
                            label="פאזה"
                          >
                            {phaseOptions.map((phase) => (
                              <MenuItem key={`${tf}-${phase}`} value={phase}>
                                {phase || 'אין'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* Specific candle times based on main timeframe */}
            {tradeForm.triggerAppeared === 'yes' && tradeForm.mainTimeframe && (
              <Box sx={styles.candleTimesContainer}>
                <Box sx={styles.formHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="info.main">
                    {getCandleTimeframeLabel()}
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                    borderRadius: 8,
                    padding: '8px',
                    marginLeft: 10
                  }}>
                    <ScheduleIcon />
                  </Box>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="נר ספייק ראשון"
                      type="time"
                      name="firstSpikeCandle"
                      value={tradeForm.firstSpikeCandle}
                      onChange={handleTradeFormChange}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={styles.inputField}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="נר ציר פנימי"
                      type="time"
                      name="internalPivotCandle"
                      value={tradeForm.internalPivotCandle}
                      onChange={handleTradeFormChange}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={styles.inputField}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="נר ספייק שני"
                      type="time"
                      name="secondSpikeCandle"
                      value={tradeForm.secondSpikeCandle}
                      onChange={handleTradeFormChange}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={styles.inputField}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="נר בוס ואישור כניסה"
                      type="time"
                      name="bossConfirmationCandle"
                      value={tradeForm.bossConfirmationCandle}
                      onChange={handleTradeFormChange}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={styles.inputField}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box sx={styles.formStepButtons}>
              <Button 
                variant="contained"
                color="primary"
                startIcon={<NavigateNextIcon />}
                onClick={handleNextStep}
                disabled={!tradeForm.mainTimeframe || !tradeForm.tradeDirection}
                sx={styles.formNavigationButton}
              >
                המשך לכניסה
              </Button>
              <Button 
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                onClick={handlePrevStep}
                sx={styles.formNavigationButton}
              >
                חזרה
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Entry */}
        {formStep === 2 && tradeForm.triggerAppeared === 'yes' && (
          <Box sx={{ p: 3 }}>
            <Box sx={styles.formSection}>
              <Box sx={styles.formHeader}>
                <Typography variant="subtitle1" fontWeight="bold">
                  פרטי כניסה
                </Typography>
                <Box sx={styles.formHeaderIcon}>
                  <CallMadeIcon />
                </Box>
              </Box>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                הגדר את פרטי הכניסה שלך ורמות פיבונאצ'י
              </Typography>
              
              <Grid container spacing={3}>
                {/* Fibonacci for all triggers */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                    <InputLabel>רמת פיבונאצ'י בכניסה</InputLabel>
                    <Select
                      name="entryFiboLevel"
                      value={tradeForm.entryFiboLevel}
                      onChange={handleFiboChange}
                      label="רמת פיבונאצ'י בכניסה"
                    >
                      {fiboLevels.map(level => (
                        <MenuItem key={level} value={level}>{level}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                    <InputLabel>רמת פיבונאצ'י שהסטופ ישב עליה</InputLabel>
                    <Select
                      name="startFiboLevel"
                      value={tradeForm.startFiboLevel}
                      onChange={handleFiboChange}
                      label="רמת פיבונאצ'י שהסטופ ישב עליה"
                    >
                      {fiboLevels.map(level => (
                        <MenuItem key={level} value={level}>{level}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={12}>
                  <TextField
                    label="אחוז סטופ"
                    name="stopPercentage"
                    type="number"
                    value={tradeForm.stopPercentage}
                    onChange={handleTradeFormChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={styles.inputField}
                  />
                </Grid>
              </Grid>

              {/* TP Recommendation based on Phases and Fibonacci levels */}
              {tradeForm.entryFiboLevel && tradeForm.startFiboLevel && (
                <Box sx={styles.fiboBox}>
                  <SignalCellularAltIcon sx={styles.fiboIcon} />
                  <Typography variant="h6" sx={styles.fiboTitle}>
                    <SignalCellularAltIcon sx={{ ml: 1 }} />
                    המלצת יעד TP ופיבונאצ'י:
                  </Typography>
                  <Typography variant="body1" sx={styles.fiboValue}>
                    {tpResult.recommendation || "נא להשלים את כל פרטי הפאזות"}
                  </Typography>
                  {tpResult.targetFiboLevels && tpResult.targetFiboLevels.length > 0 && (
                    <Box sx={styles.tpDetails}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: theme.palette.info.main, mb: 1 }}>
                        רמות פיבונאצ'י יעד:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {tpResult.targetFiboLevels.map((level, index) => (
                          <Chip 
                            key={index}
                            label={level}
                            variant="outlined"
                            color="info"
                            size="small"
                            icon={<SignalCellularAltIcon />}
                            sx={{ fontWeight: 'medium' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* TP Recommendation */}
              {tpResult.recommendation && (
                <Box sx={styles.enhancedTPBox}>
                  <FlagIcon sx={styles.tpIcon} />
                  <Typography variant="h6" sx={styles.tpTitle}>
                    <DoneAllIcon sx={{ ml: 1 }} />
                    המלצת TP:
                  </Typography>
                  <Typography variant="body1" sx={styles.tpValue}>
                    {tpResult.recommendation}
                  </Typography>
                  {tpResult.phasesWithC && tpResult.phasesWithC.length > 0 && (
                    <Box sx={styles.tpDetails}>
                      <Typography variant="body2">
                        טיימפריימים עם פאזה C: {tpResult.phasesWithC.join(', ')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              
              {/* R Values for ICE/CREEK */}
              {tpResult.timeframesToAskR && tpResult.timeframesToAskR.length > 0 && (
                <Box sx={styles.rValueSection}>
                  <Typography variant="subtitle1" sx={styles.rValueTitle}>
                    <EqualizerIcon sx={{ ml: 1 }} />
                    ערכי R שהושגו ב-ICE/CREEK לפי טיימפריים
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {tpResult.timeframesToAskR.map((timeframe) => (
                      <Grid item xs={12} sm={6} md={4} key={timeframe}>
                        <Box 
                          sx={{
                            ...styles.rValueCard,
                            ...(tradeForm.achievedRValues[timeframe] ? styles.rValueFilled : {})
                          }}
                          onClick={() => openRValueDialog(timeframe, 'normal')}
                        >
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              טיימפריים {timeframe} - ICE/CREEK
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {tradeForm.achievedRValues[timeframe] 
                                ? `הושג ${tradeForm.achievedRValues[timeframe]}R` 
                                : 'לחץ להזנת ערך'}
                            </Typography>
                          </Box>
                          <Button 
                            size="small" 
                            variant={tradeForm.achievedRValues[timeframe] ? "outlined" : "contained"}
                            color={tradeForm.achievedRValues[timeframe] ? "success" : "primary"}
                            sx={{ borderRadius: 8, minWidth: 90 }}
                          >
                            {tradeForm.achievedRValues[timeframe] ? "ערוך" : "הזן"}
                          </Button>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>

            <Box sx={styles.formStepButtons}>
              <Button 
                variant="contained"
                color="primary"
                startIcon={<NavigateNextIcon />}
                onClick={handleNextStep}
                disabled={
                  !tradeForm.stopPercentage || 
                  !tradeForm.entryFiboLevel || 
                  !tradeForm.startFiboLevel
                }
                sx={styles.formNavigationButton}
              >
                המשך ליציאה
              </Button>
              <Button 
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                onClick={handlePrevStep}
                sx={styles.formNavigationButton}
              >
                חזרה
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 4: Exit */}
        {formStep === 3 && tradeForm.triggerAppeared === 'yes' && (
          <Box sx={{ p: 3 }}>
            <Box sx={styles.formSection}>
              <Box sx={styles.formHeader}>
                <Typography variant="subtitle1" fontWeight="bold">
                  פרטי יציאה ותוצאות
                </Typography>
                <Box sx={styles.formHeaderIcon}>
                  <CallReceivedIcon />
                </Box>
              </Box>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                הגדר את תוצאת העסקה ונקודות היציאה
              </Typography>
              
              <Grid container spacing={3}>
                {/* Trade outcome */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                    <InputLabel>תוצאת העסקה</InputLabel>
                    <Select
                      name="outcome"
                      value={tradeForm.outcome}
                      onChange={handleTradeFormChange}
                      label="תוצאת העסקה"
                    >
                      {outcomeOptions.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Fibonacci Reaction Levels */}
                <Grid item xs={12} md={8}>
                  <Autocomplete
                    multiple
                    options={fiboLevels}
                    value={tradeForm.reactionFiboLevels}
                    onChange={handleReactionFiboChange}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="רמות פיבונאצ'י עם תגובה מהפכנית שגרמה לציר פנימי"
                        placeholder="בחר רמות"
                        fullWidth
                        sx={styles.inputField}
                      />
                    )}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        paddingY: '4.5px',
                      }
                    }}
                  />
                </Grid>
                
                {/* Target Fibonacci Levels Reached */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    multiple
                    options={fiboLevels}
                    value={tradeForm.targetFiboLevel}
                    onChange={handleTargetFiboChange}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="יעדי פיבו"
                        placeholder="בחר יעדים"
                        fullWidth
                        sx={styles.inputField}
                      />
                    )}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        paddingY: '4.5px',
                      }
                    }}
                  />
                </Grid>
                
                {/* Max Fibonacci Level Reached */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                    <InputLabel>רמת פיבונאצ'י מקסימלית שהמחיר הגיע אליה</InputLabel>
                    <Select
                      name="maxFiboLevel"
                      value={tradeForm.maxFiboLevel}
                      onChange={handleFiboChange}
                      label="רמת פיבונאצ'י מקסימלית שהמחיר הגיע אליה"
                    >
                      {fiboLevels.map(level => (
                        <MenuItem key={level} value={level}>{level}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* TP Milestones Reached */}
                <Grid item xs={12} md={12}>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={getTPOptionsForOutcome(tradeForm.outcome)}
                    value={tradeForm.tpMilestonesReached}
                    onChange={handleTradeFormMilestonesReachedChange}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="יעדי TP שהושגו"
                        placeholder="בחר יעדים או הקלד ערך מותאם"
                        fullWidth
                        sx={styles.inputField}
                      />
                    )}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        paddingY: '4.5px',
                      }
                    }}
                  />
                </Grid>
                
                {/* Exit point & Final TP (if Take Profit) */}
                {tradeForm.outcome !== 'Stop Loss' ? (
                  <>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        freeSolo
                        options={getTPOptionsForOutcome(tradeForm.outcome).concat(['סטופ'])}
                        value={tradeForm.tpMilestoneExit}
                        onChange={(event, newValue) => {
                          setTradeForm((prev) => ({ ...prev, tpMilestoneExit: newValue }));
                        }}
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="נקודת יציאה בפועל"
                            placeholder="בחר או הקלד ערך מותאם"
                            fullWidth
                            sx={styles.inputField}
                          />
                        )}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            paddingY: '4.5px',
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={tpOptions}
                        value={tradeForm.finalTP}
                        onChange={handleFinalTPChange}
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="TP סופי בסגירת החלון הנוכחי"
                            placeholder="בחר TP סופי או הקלד ערך מותאם"
                            fullWidth
                            sx={styles.inputField}
                          />
                        )}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            paddingY: '4.5px',
                          }
                        }}
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                      <InputLabel>סיבת סטופ לוס</InputLabel>
                      <Select
                        value={stopReasonValue}
                        onChange={(e) => setStopReasonValue(e.target.value)}
                        label="סיבת סטופ לוס"
                      >
                        <MenuItem value="חלק מהסטטיסטיקה (הביצוע היה מושלם)">
                          חלק מהסטטיסטיקה (הביצוע היה מושלם)
                        </MenuItem>
                        <MenuItem value="חוסר סינרגיה">חוסר סינרגיה</MenuItem>
                        <MenuItem value="שורט ב-ace בפאזה B">שורט ב-ace בפאזה B</MenuItem>
                        <MenuItem value="לונג ב-ace בפאזה B">לונג ב-ace בפאזה B</MenuItem>
                        <MenuItem value="הצבת סטופ חמדנית מדי">הצבת סטופ חמדנית מדי</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>

              {/* R Values for Fibonacci levels */}
              <Box sx={styles.rValueSection}>
                <Typography variant="subtitle1" sx={styles.rValueTitle}>
                  <EqualizerIcon sx={{ ml: 1 }} />
                  כמה R הגיע לשם בעסקה - ברמות פיבונאצ'י
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box 
                      sx={{
                        ...styles.rValueCard,
                        ...(tradeForm.fiboRValues['1.414'] ? styles.rValueFilled : {})
                      }}
                      onClick={() => openRValueDialog('', 'fibo', '1.414')}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          רמת פיבו 141.4%
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {tradeForm.fiboRValues['1.414'] 
                            ? `הושג ${tradeForm.fiboRValues['1.414']}R` 
                            : 'לחץ להזנת ערך'}
                        </Typography>
                      </Box>
                      <Button 
                        size="small" 
                        variant={tradeForm.fiboRValues['1.414'] ? "outlined" : "contained"}
                        color={tradeForm.fiboRValues['1.414'] ? "success" : "primary"}
                        sx={{ borderRadius: 8, minWidth: 90 }}
                      >
                        {tradeForm.fiboRValues['1.414'] ? "ערוך" : "הזן"}
                      </Button>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4}>
                    <Box 
                      sx={{
                        ...styles.rValueCard,
                        ...(tradeForm.fiboRValues['1.618'] ? styles.rValueFilled : {})
                      }}
                      onClick={() => openRValueDialog('', 'fibo', '1.618')}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          רמת פיבו 161.8%
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {tradeForm.fiboRValues['1.618'] 
                            ? `הושג ${tradeForm.fiboRValues['1.618']}R` 
                            : 'לחץ להזנת ערך'}
                        </Typography>
                      </Box>
                      <Button 
                        size="small" 
                        variant={tradeForm.fiboRValues['1.618'] ? "outlined" : "contained"}
                        color={tradeForm.fiboRValues['1.618'] ? "success" : "primary"}
                        sx={{ borderRadius: 8, minWidth: 90 }}
                      >
                        {tradeForm.fiboRValues['1.618'] ? "ערוך" : "הזן"}
                      </Button>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4}>
                    <Box 
                      sx={{
                        ...styles.rValueCard,
                        ...(tradeForm.fiboRValues['2.236'] ? styles.rValueFilled : {})
                      }}
                      onClick={() => openRValueDialog('', 'fibo', '2.236')}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          רמת פיבו 223.6%
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {tradeForm.fiboRValues['2.236'] 
                            ? `הושג ${tradeForm.fiboRValues['2.236']}R` 
                            : 'לחץ להזנת ערך'}
                        </Typography>
                      </Box>
                      <Button 
                        size="small" 
                        variant={tradeForm.fiboRValues['2.236'] ? "outlined" : "contained"}
                        color={tradeForm.fiboRValues['2.236'] ? "success" : "primary"}
                        sx={{ borderRadius: 8, minWidth: 90 }}
                      >
                        {tradeForm.fiboRValues['2.236'] ? "ערוך" : "הזן"}
                      </Button>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4}>
                    <Box 
                      sx={{
                        ...styles.rValueCard,
                        ...(tradeForm.fiboRValues['2.618'] ? styles.rValueFilled : {})
                      }}
                      onClick={() => openRValueDialog('', 'fibo', '2.618')}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          רמת פיבו 261.8%
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {tradeForm.fiboRValues['2.618'] 
                            ? `הושג ${tradeForm.fiboRValues['2.618']}R` 
                            : 'לחץ להזנת ערך'}
                        </Typography>
                      </Box>
                      <Button 
                        size="small" 
                        variant={tradeForm.fiboRValues['2.618'] ? "outlined" : "contained"}
                        color={tradeForm.fiboRValues['2.618'] ? "success" : "primary"}
                        sx={{ borderRadius: 8, minWidth: 90 }}
                      >
                        {tradeForm.fiboRValues['2.618'] ? "ערוך" : "הזן"}
                      </Button>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4}>
                    <Box 
                      sx={{
                        ...styles.rValueCard,
                        ...(tradeForm.fiboRValues['max'] ? styles.rValueFilled : {})
                      }}
                      onClick={() => openRValueDialog('', 'fibo', 'max')}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          MAX R
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {tradeForm.fiboRValues['max'] 
                            ? `הושג ${tradeForm.fiboRValues['max']}R` 
                            : 'לחץ להזנת ערך'}
                        </Typography>
                      </Box>
                      <Button 
                        size="small" 
                        variant={tradeForm.fiboRValues['max'] ? "outlined" : "contained"}
                        color={tradeForm.fiboRValues['max'] ? "success" : "primary"}
                        sx={{ borderRadius: 8, minWidth: 90 }}
                      >
                        {tradeForm.fiboRValues['max'] ? "ערוך" : "הזן"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Box sx={styles.formStepButtons}>
              <Button 
                variant="contained"
                color="primary"
                startIcon={<NavigateNextIcon />}
                onClick={handleNextStep}
                disabled={!tradeForm.outcome || tradeForm.tpMilestonesReached.length === 0 || 
                         ((tradeForm.outcome === 'Take Profit' || tradeForm.outcome === 'Break Even') && !tradeForm.tpMilestoneExit) ||
                         (tradeForm.outcome === 'Stop Loss' && !stopReasonValue)}
                sx={styles.formNavigationButton}
              >
                המשך לסיכום
              </Button>
              <Button 
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                onClick={handlePrevStep}
                sx={styles.formNavigationButton}
              >
                חזרה
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 5: Summary */}
        {formStep === 4 && (
          <Box sx={{ p: 3 }}>
            <Box sx={styles.summaryBox}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckBoxOutlinedIcon sx={{ ml: 1 }} />
                סיכום נתוני העסקה
              </Typography>
              
              <Paper sx={{ 
                p: 3, 
                borderRadius: 3, 
                bgcolor: alpha(theme.palette.background.paper, 0.5),
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)'
              }}>
                <Grid container spacing={2}>
                  {/* Basic Details */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom sx={{ 
                      pb: 1, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <TuneIcon sx={{ ml: 1, fontSize: 20 }} />
                      פרטים בסיסיים
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">סימול:</Typography>
                    <Typography variant="body1" fontWeight="500">{tradeForm.symbol}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">תאריך פתיחה:</Typography>
                    <Typography variant="body1" fontWeight="500">{tradeForm.date}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">שעת פתיחה:</Typography>
                    <Typography variant="body1" fontWeight="500">{tradeForm.time}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">יום בשבוע:</Typography>
                    <Typography variant="body1" fontWeight="500">{tradeForm.dayOfWeek}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">תאריך סגירה:</Typography>
                    <Typography variant="body1" fontWeight="500">{tradeForm.closeDate || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">שעת סגירה:</Typography>
                    <Typography variant="body1" fontWeight="500">{tradeForm.closeTime || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">משך זמן (דקות):</Typography>
                    <Typography variant="body1" fontWeight="500">{tradeForm.duration || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">האם היה טריגר:</Typography>
                    <Typography variant="body1" fontWeight="500">
                      {tradeForm.triggerAppeared === 'yes' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                          כן
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                          לא
                        </Box>
                      )}
                    </Typography>
                  </Grid>

                  {/* Trigger details */}
                  {tradeForm.triggerAppeared === 'yes' && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom sx={{
                          pb: 1, 
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          display: 'flex',
                          alignItems: 'center',
                          mt: 2
                        }}>
                          <ApartmentIcon sx={{ ml: 1, fontSize: 20 }} />
                          פרטי טריגר
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">טריגר תואם:</Typography>
                        <Typography variant="body1" fontWeight="500">{tradeForm.matchedTrigger}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">סשן:</Typography>
                        <Typography variant="body1" fontWeight="500">{tradeForm.session}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">אחוז סטופ:</Typography>
                        <Typography variant="body1" fontWeight="500">{tradeForm.stopPercentage}%</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">תוצאת עסקה:</Typography>
                        <Typography 
                          variant="body1" 
                          fontWeight="bold"
                          color={tradeForm.outcome === 'Take Profit' ? 'success.main' : 'error.main'}
                        >
                          {tradeForm.outcome}
                        </Typography>
                      </Grid>
                      
                      {/* Phase Analysis */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom sx={{
                          pb: 1, 
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          display: 'flex',
                          alignItems: 'center',
                          mt: 2
                        }}>
                          <BarChartIcon sx={{ ml: 1, fontSize: 20 }} />
                          ניתוח פאזות וטיימפריימים
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">כיוון עסקה:</Typography>
                        <Typography variant="body1" fontWeight="500" sx={{ display: 'flex', alignItems: 'center' }}>
                          {tradeForm.tradeDirection === 'long' ? (
                            <>
                              <TrendingUpIcon color="success" fontSize="small" sx={{ ml: 0.5 }} />
                              לונג
                            </>
                          ) : (
                            <>
                              <TrendingDownIcon color="error" fontSize="small" sx={{ ml: 0.5 }} />
                              שורט
                            </>
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">טיימפריים ראשי:</Typography>
                        <Typography variant="body1" fontWeight="500">{tradeForm.mainTimeframe}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <Typography variant="body2" color="text.secondary">המלצת TP:</Typography>
                        <Typography variant="body1" fontWeight="500" color="success.main">
                          {tradeForm.tpRecommendation || '-'}
                        </Typography>
                      </Grid>
                      
                      {/* Phases with C */}
                      {tradeForm.phasesWithC && tradeForm.phasesWithC.length > 0 && (
                        <Grid item xs={12} sm={6} md={6}>
                          <Typography variant="body2" color="text.secondary">טיימפריימים עם פאזה C:</Typography>
                          <Typography variant="body1" fontWeight="500">
                            {tradeForm.phasesWithC.join(', ')}
                          </Typography>
                        </Grid>
                      )}
                      
                      {/* R Values - ICE/CREEK */}
                      {Object.keys(tradeForm.achievedRValues).length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">ערכי R שהושגו (ICE/CREEK):</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {Object.entries(tradeForm.achievedRValues).map(([timeframe, value]) => (
                              <Chip 
                                key={timeframe}
                                label={`${timeframe}: ${value}R`}
                                color="success"
                                variant="outlined"
                                size="small"
                                sx={{ fontWeight: 'medium' }}
                              />
                            ))}
                          </Box>
                        </Grid>
                      )}
                      
                      {/* R Values - Fibonacci */}
                      {Object.keys(tradeForm.fiboRValues).some(key => tradeForm.fiboRValues[key]) && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">ערכי R שהושגו ברמות פיבונאצ'י:</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {Object.entries(tradeForm.fiboRValues).map(([level, value]) => {
                              if (!value) return null;
                              const levelLabel = level === 'max' ? 'MAX' : 
                                               level === '1.414' ? '141.4%' :
                                               level === '1.618' ? '161.8%' :
                                               level === '2.236' ? '223.6%' : 
                                               level === '2.618' ? '261.8%' : level;
                              return (
                                <Chip 
                                  key={level}
                                  label={`${levelLabel}: ${value}R`}
                                  color="info"
                                  variant="outlined"
                                  size="small"
                                  sx={{ fontWeight: 'medium' }}
                                />
                              );
                            })}
                          </Box>
                        </Grid>
                      )}
                      
                      {/* Fibonacci levels */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">רמת פיבונאצ'י בכניסה:</Typography>
                        <Typography variant="body1" fontWeight="500" color="info.main">
                          {tradeForm.entryFiboLevel || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">רמת פיבונאצ'י שהסטופ ישב עליה:</Typography>
                        <Typography variant="body1" fontWeight="500" color="info.main">
                          {tradeForm.startFiboLevel || '-'}
                        </Typography>
                      </Grid>
                      {tradeForm.targetFiboLevel && tradeForm.targetFiboLevel.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">יעדי פיבו שהושגו:</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {tradeForm.targetFiboLevel.map((level, index) => (
                              <Chip 
                                key={index} 
                                label={level} 
                                size="small" 
                                color="info" 
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Grid>
                      )}
                      {tradeForm.maxFiboLevel && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary">רמת פיבונאצ'י מקסימלית:</Typography>
                          <Typography variant="body1" fontWeight="500" color="info.main">
                            {tradeForm.maxFiboLevel}
                          </Typography>
                        </Grid>
                      )}
                      
                      {/* Fibonacci Reaction Levels */}
                      {tradeForm.reactionFiboLevels && tradeForm.reactionFiboLevels.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">רמות פיבונאצ'י עם תגובה מהפכנית:</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {tradeForm.reactionFiboLevels.map((level, index) => (
                              <Chip 
                                key={index} 
                                label={level} 
                                size="small" 
                                color="info" 
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Grid>
                      )}
                      
                      {/* Exit Details */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom sx={{
                          pb: 1, 
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          display: 'flex',
                          alignItems: 'center',
                          mt: 2
                        }}>
                          <CallReceivedIcon sx={{ ml: 1, fontSize: 20 }} />
                          פרטי יציאה
                        </Typography>
                      </Grid>
                      
                      {/* TP Milestones */}
                      {tradeForm.tpMilestonesReached && tradeForm.tpMilestonesReached.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">יעדי TP שהושגו:</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {tradeForm.tpMilestonesReached.map((tp, index) => (
                              <Chip 
                                key={index} 
                                label={tp} 
                                size="small" 
                                color="success" 
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Grid>
                      )}
                      
                      {tradeForm.outcome === 'Take Profit' && (
                        <>
                          <Grid item xs={12} sm={6} md={6}>
                            <Typography variant="body2" color="text.secondary">נקודת יציאה בפועל:</Typography>
                            <Typography variant="body1" fontWeight="500" color="success.main">
                              {tradeForm.tpMilestoneExit || '-'}
                            </Typography>
                          </Grid>
                          {tradeForm.finalTP && tradeForm.finalTP.length > 0 && (
                            <Grid item xs={12} sm={6} md={6}>
                              <Typography variant="body2" color="text.secondary">TP סופי בסגירת החלון:</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {tradeForm.finalTP.map((tp, index) => (
                                  <Chip 
                                    key={index} 
                                    label={tp} 
                                    size="small" 
                                    color="success" 
                                    variant="filled"
                                  />
                                ))}
                              </Box>
                            </Grid>
                          )}
                        </>
                      )}
                      
                      {tradeForm.outcome === 'Stop Loss' && stopReasonValue && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">סיבת סטופ לוס:</Typography>
                          <Typography variant="body1" fontWeight="500" color="error.main">
                            {stopReasonValue}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                  
                  {/* Reports */}
                  {tradeForm.reports && tradeForm.reports.length > 0 && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom sx={{
                          pb: 1, 
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          display: 'flex',
                          alignItems: 'center',
                          mt: 2
                        }}>
                          <EventNoteIcon sx={{ ml: 1, fontSize: 20 }} />
                          דיווחים
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {tradeForm.reports.map((report, index) => (
                            <Box key={index} sx={{ 
                              p: 1.5, 
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              borderRadius: 1,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
                            }}>
                              <Typography variant="body2" fontWeight="500">
                                {report.report} - {report.time}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Grid>
                    </>
                  )}
                  
                  {/* Candle Times */}
                  {tradeForm.triggerAppeared === 'yes' && (tradeForm.firstSpikeCandle || tradeForm.internalPivotCandle || tradeForm.secondSpikeCandle || tradeForm.bossConfirmationCandle) && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom sx={{
                          pb: 1, 
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          display: 'flex',
                          alignItems: 'center',
                          mt: 2
                        }}>
                          <ScheduleIcon sx={{ ml: 1, fontSize: 20 }} />
                          זמני נרות ספציפיים
                        </Typography>
                      </Grid>
                      {tradeForm.firstSpikeCandle && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary">נר ספייק ראשון:</Typography>
                          <Typography variant="body1" fontWeight="500">{tradeForm.firstSpikeCandle}</Typography>
                        </Grid>
                      )}
                      {tradeForm.internalPivotCandle && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary">נר ציר פנימי:</Typography>
                          <Typography variant="body1" fontWeight="500">{tradeForm.internalPivotCandle}</Typography>
                        </Grid>
                      )}
                      {tradeForm.secondSpikeCandle && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary">נר ספייק שני:</Typography>
                          <Typography variant="body1" fontWeight="500">{tradeForm.secondSpikeCandle}</Typography>
                        </Grid>
                      )}
                      {tradeForm.bossConfirmationCandle && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary">נר בוס ואישור:</Typography>
                          <Typography variant="body1" fontWeight="500">{tradeForm.bossConfirmationCandle}</Typography>
                        </Grid>
                      )}
                    </>
                  )}
                  
                  {/* Chart Images */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom sx={{
                      pb: 1, 
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      display: 'flex',
                      alignItems: 'center',
                      mt: 2
                    }}>
                      <ImageIcon sx={{ ml: 1, fontSize: 20 }} />
                      תמונות גרף
                    </Typography>
                  </Grid>
                  {tradeForm.linkBefore && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>תמונה לפני:</Typography>
                      <Box 
                        component="img"
                        src={tradeForm.linkBefore}
                        alt="תמונה לפני"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div style="padding: 20px; text-align: center; color: #999;">תמונה לא זמינה</div>';
                        }}
                      />
                    </Grid>
                  )}
                  {tradeForm.linkAfter && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>תמונה אחרי:</Typography>
                      <Box 
                        component="img"
                        src={tradeForm.linkAfter}
                        alt="תמונה אחרי"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div style="padding: 20px; text-align: center; color: #999;">תמונה לא זמינה</div>';
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Box>

            <Box sx={styles.formStepButtons}>
              <Button 
                type="submit"
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                sx={styles.saveButton}
              >
                שמור עסקה
              </Button>
              <Button 
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                onClick={handlePrevStep}
                sx={styles.formNavigationButton}
              >
                חזרה
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Dialogs */}
      
      {/* Missing Fields Dialog */}
      <Dialog 
        open={missingFieldsDialogOpen} 
        onClose={handleMissingFieldsBack} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            direction: 'rtl',
            textAlign: 'right'
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          textAlign: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          bgcolor: alpha(theme.palette.warning.main, 0.05)
        }}>
          <Typography variant="h6" fontWeight="bold" color="warning.main" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ErrorOutlineIcon sx={{ ml: 1 }} />
            שדות חסרים
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" gutterBottom>
            שים לב: השדות הבאים חסרים:
          </Typography>
          <Paper 
            sx={{ 
              p: 2.5, 
              bgcolor: alpha(theme.palette.warning.main, 0.05),
              borderRadius: 2,
              my: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            }}
          >
            <ul style={{ margin: 0, paddingLeft: 20, paddingRight: 0 }}>
              {missingFields.map((field, index) => (
                <li key={index}>
                  <Typography variant="body2" color="text.secondary">{field}</Typography>
                </li>
              ))}
            </ul>
          </Paper>
          <Typography variant="body1">
            האם ברצונך לרשום את העסקה בכל מקרה?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={handleMissingFieldsBack}
            variant="outlined"
            sx={styles.formNavigationButton}
          >
            חזרה
          </Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={handleMissingFieldsRecord}
            sx={styles.formNavigationButton}
          >
            שמור בכל מקרה
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stop Reason Dialog */}
      <Dialog
        open={stopReasonDialogOpen}
        onClose={handleCloseStopReasonDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            direction: 'rtl',
            textAlign: 'right'
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          textAlign: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          bgcolor: alpha(theme.palette.error.main, 0.05)
        }}>
          <Typography variant="h6" fontWeight="bold" color="error" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ErrorOutlineIcon sx={{ ml: 1 }} />
            סיבת סטופ לוס
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ mt: 1, ...styles.inputField }}>
            <InputLabel>בחר סיבה</InputLabel>
            <Select
              label="בחר סיבה"
              value={stopReasonValue}
              onChange={(e) => setStopReasonValue(e.target.value)}
            >
              <MenuItem value="חלק מהסטטיסטיקה (הביצוע היה מושלם)">
                חלק מהסטטיסטיקה (הביצוע היה מושלם)
              </MenuItem>
              <MenuItem value="חוסר סינרגיה">חוסר סינרגיה</MenuItem>
              <MenuItem value="שורט ב-ACE בפאזה B">שורט ב-ACE בפאזה B</MenuItem>
              <MenuItem value="לונג ב-ACE בפאזה B">לונג ב-ACE בפאזה B</MenuItem>
              <MenuItem value="הצבת סטופ חמדנית מדי">הצבת סטופ חמדנית מדי</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={handleCloseStopReasonDialog}
            variant="outlined"
            sx={styles.formNavigationButton}
          >
            ביטול
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSaveStopReason}
            sx={styles.formNavigationButton}
          >
            שמור
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={handleCloseReportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            direction: 'rtl',
            textAlign: 'right'
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          textAlign: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.05)
        }}>
          <Typography variant="h6" fontWeight="bold" color="primary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EventNoteIcon sx={{ ml: 1 }} />
            הוסף דיווח
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
              <InputLabel>סוג דיווח</InputLabel>
              <Select
                name="report"
                value={newReport.report}
                onChange={handleReportChange}
                label="סוג דיווח"
              >
                {reportOptions.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="שעה"
              type="time"
              value={newReport.time}
              onChange={(e) => setNewReport(prev => ({ ...prev, time: e.target.value }))}
              fullWidth
              variant="outlined"
              size="small"
              sx={styles.inputField}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={handleCloseReportDialog}
            variant="outlined"
            sx={styles.formNavigationButton}
          >
            ביטול
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleAddReport}
            sx={styles.formNavigationButton}
          >
            הוסף דיווח
          </Button>
        </DialogActions>
      </Dialog>

      {/* R Value Dialog */}
      <Dialog
        open={rValueDialogOpen}
        onClose={() => setRValueDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            direction: 'rtl',
            textAlign: 'right'
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          textAlign: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.05)
        }}>
          <Typography variant="h6" fontWeight="bold" color="primary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EqualizerIcon sx={{ ml: 1 }} />
            {currentRValueType === 'fibo' ? (
              <>
                ערך R עבור רמת פיבונאצ'י {
                  currentFiboLevel === 'max' ? 'MAX' :
                  currentFiboLevel === '1.414' ? '141.4%' :
                  currentFiboLevel === '1.618' ? '161.8%' :
                  currentFiboLevel === '2.236' ? '223.6%' :
                  currentFiboLevel === '2.618' ? '261.8%' : ''
                }
              </>
            ) : (
              <>ערך R עבור טיימפריים {currentRValueTimeframe} (ICE/CREEK)</>
            )}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {currentRValueType === 'fibo' 
                ? `כמה R הושגו עד לרמת הפיבונאצ'י הזו?`
                : `כמה R הושגו עד ל-ICE/CREEK בטיימפריים זה?`}
            </Typography>
            <TextField
              label="ערך R"
              type="number"
              inputProps={{ min: "0", step: "0.5" }}
              value={currentRValue}
              onChange={handleRValueChange}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                ...styles.inputField,
                mt: 1.5
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={() => setRValueDialogOpen(false)}
            variant="outlined"
            sx={styles.formNavigationButton}
          >
            ביטול
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSaveRValue}
            sx={styles.formNavigationButton}
          >
            שמור
          </Button>
        </DialogActions>
      </Dialog>

      {/* Another Trigger Dialog */}
      <Dialog
        open={anotherTriggerDialogOpen}
        onClose={() => setAnotherTriggerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            direction: 'rtl',
            textAlign: 'right'
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          textAlign: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          bgcolor: alpha(theme.palette.info.main, 0.05)
        }}>
          <Typography variant="h6" fontWeight="bold" color="info.main" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AddCircleOutlineIcon sx={{ ml: 1 }} />
            טריגר נוסף
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {anotherTriggerStep === 0 && (
            <>
              <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
                האם יש עוד טריגר באותו יום?
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleAnotherTriggerResponse(true)}
                  sx={{ minWidth: 100 }}
                >
                  כן
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleAnotherTriggerResponse(false)}
                  sx={{ minWidth: 100 }}
                >
                  לא
                </Button>
              </Box>
            </>
          )}
          
          {anotherTriggerStep === 1 && (
            <>
              <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
                איזה טריגר?
              </Typography>
              
              <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                <InputLabel>סוג טריגר</InputLabel>
                <Select
                  value={anotherTriggerType}
                  onChange={(e) => setAnotherTriggerType(e.target.value)}
                  label="סוג טריגר"
                >
                  {triggerOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAnotherTriggerConfirm}
                  disabled={!anotherTriggerType}
                  sx={{ minWidth: 100 }}
                >
                  אישור
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setAnotherTriggerDialogOpen(false);
                    setAnotherTriggerType('');
                    setAnotherTriggerStep(0);
                  }}
                  sx={{ minWidth: 100 }}
                >
                  ביטול
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </LocalizationProvider>
  );
};

export default TradeForm;